import { Email } from '../value-objects/email.vo';
import { Rut } from '../value-objects/rut.vo';

export interface UserProps {
  id: string;
  email: Email;
  fullName?: string | null;
  rut?: string | null;
  role?: string;
  status?: string;
  permissions?: string[];
  createdAt?: Date;
  updatedAt?: Date;
  lastLoginAt?: Date | null;
}

export class User {
  private readonly _id: string;
  private readonly _email: Email;
  private readonly _fullName: string | null;
  private readonly _rut: string | null;
  private readonly _role: string;
  private readonly _status: string;
  private readonly _permissions: string[];
  private readonly _createdAt: Date;
  private readonly _updatedAt: Date;
  private readonly _lastLoginAt: Date | null;

  private constructor(props: UserProps) {
    this._id = props.id;
    this._email = props.email;
    this._fullName = props.fullName ?? null;
    this._rut = props.rut ?? null;
    this._role = props.role ?? 'operator';
    this._status = props.status ?? 'ACTIVE';
    this._permissions = props.permissions ?? [];
    this._createdAt = props.createdAt ?? new Date();
    this._updatedAt = props.updatedAt ?? new Date();
    this._lastLoginAt = props.lastLoginAt ?? null;
  }

  public static create(props: {
    id: string;
    email: string;
    fullName?: string | null;
    rut?: string | null;
    role?: string;
    status?: string;
    permissions?: string[];
    createdAt?: Date | string;
    updatedAt?: Date | string;
    lastLoginAt?: Date | string | null;
  }): User {
    return new User({
      id: props.id,
      email: Email.create(props.email),
      fullName: props.fullName,
      rut: props.rut ? Rut.clean(props.rut) : null,
      role: props.role,
      status: props.status,
      permissions: props.permissions,
      createdAt: props.createdAt ? new Date(props.createdAt) : new Date(),
      updatedAt: props.updatedAt ? new Date(props.updatedAt) : new Date(),
      lastLoginAt: props.lastLoginAt ? new Date(props.lastLoginAt) : null,
    });
  }

  public get id(): string {
    return this._id;
  }

  public get email(): Email {
    return this._email;
  }

  public get emailValue(): string {
    return this._email.value;
  }

  public get fullName(): string | null {
    return this._fullName;
  }

  public get rut(): string | null {
    return this._rut;
  }

  public get rutValue(): string | null {
    return this._rut;
  }

  public get rutFormatted(): string | null {
    return this._rut ? Rut.format(this._rut) : null;
  }

  public get role(): string {
    return this._role;
  }

  public get status(): string {
    return this._status;
  }

  public get isActive(): boolean {
    return this._status === 'ACTIVE' || this._status === 'active';
  }

  public get permissions(): string[] {
    return [...this._permissions];
  }

  public get createdAt(): Date {
    return this._createdAt;
  }

  public get updatedAt(): Date {
    return this._updatedAt;
  }

  public get lastLoginAt(): Date | null {
    return this._lastLoginAt;
  }

  public hasPermission(code: string): boolean {
    const roleNormalized = this._role?.toLowerCase();
    if (roleNormalized === 'admin' || roleNormalized === 'superadmin') return true;
    return this._permissions.includes(code);
  }

  public toJSON() {
    return {
      id: this._id,
      email: this._email.value,
      fullName: this._fullName,
      rut: this._rut,
      role: this._role,
      status: this._status,
      permissions: [...this._permissions],
      createdAt: this._createdAt,
      updatedAt: this._updatedAt,
      lastLoginAt: this._lastLoginAt,
    };
  }
}

